import { useState, useEffect } from 'react'

export default function ScreenshotCarousel({ screenshots, className = '', autoRotateInterval = 3500 }) {
    const [currentIndex, setCurrentIndex] = useState(0)

    useEffect(() => {
        if (!screenshots || screenshots.length <= 1) return

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => 
                (prevIndex + 1) % screenshots.length
            )
        }, autoRotateInterval)

        return () => clearInterval(interval)
    }, [screenshots, autoRotateInterval])

    if (!screenshots || screenshots.length === 0) {
        return null
    }

    const goToSlide = (index) => {
        setCurrentIndex(index)
    }

    const nextSlide = () => {
        setCurrentIndex((prevIndex) => 
            (prevIndex + 1) % screenshots.length
        )
    }

    const prevSlide = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === 0 ? screenshots.length - 1 : prevIndex - 1
        )
    }

    return (
        <div className={`screenshot-carousel ${className}`}>
            <div className="carousel-container">
                <div className="carousel-wrapper">
                    <img 
                        src={screenshots[currentIndex].src}
                        alt={screenshots[currentIndex].alt || `Screenshot ${currentIndex + 1}`}
                        className="carousel-image"
                    />
                </div>
                
                {screenshots.length > 1 && (
                    <>
                        <button 
                            className="carousel-nav carousel-prev" 
                            onClick={prevSlide}
                            aria-label="Previous screenshot"
                        >
                            ‹
                        </button>
                        <button 
                            className="carousel-nav carousel-next" 
                            onClick={nextSlide}
                            aria-label="Next screenshot"
                        >
                            ›
                        </button>
                        
                        <div className="carousel-dots">
                            {screenshots.map((_, index) => (
                                <button
                                    key={index}
                                    className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                                    onClick={() => goToSlide(index)}
                                    aria-label={`Go to screenshot ${index + 1}`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
            
            <style jsx>{`
                .screenshot-carousel {
                    position: relative;
                    width: 100%;
                    max-width: 800px;
                    margin: 0 auto;
                }

                .carousel-container {
                    position: relative;
                    width: 100%;
                    border-radius: 12px;
                    overflow: hidden;
                    background: #f8f9fa;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                    aspect-ratio: 16/10;
                }

                .carousel-wrapper {
                    width: 100%;
                    height: 100%;
                    position: relative;
                }

                .carousel-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: opacity 0.3s ease-in-out;
                }

                .carousel-nav {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    background: rgba(255, 255, 255, 0.9);
                    border: none;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    font-size: 24px;
                    font-weight: bold;
                    color: #333;
                    cursor: pointer;
                    z-index: 10;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    opacity: 0;
                }

                .carousel-container:hover .carousel-nav {
                    opacity: 1;
                }

                .carousel-nav:hover {
                    background: white;
                    transform: translateY(-50%) scale(1.1);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }

                .carousel-prev {
                    left: 15px;
                }

                .carousel-next {
                    right: 15px;
                }

                .carousel-dots {
                    position: absolute;
                    bottom: 15px;
                    left: 50%;
                    transform: translateX(-50%);
                    display: flex;
                    gap: 8px;
                    z-index: 10;
                }

                .carousel-dot {
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    border: none;
                    background: rgba(255, 255, 255, 0.6);
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .carousel-dot.active,
                .carousel-dot:hover {
                    background: white;
                    transform: scale(1.2);
                }

                @media (max-width: 768px) {
                    .carousel-nav {
                        width: 32px;
                        height: 32px;
                        font-size: 20px;
                    }

                    .carousel-prev {
                        left: 10px;
                    }

                    .carousel-next {
                        right: 10px;
                    }

                    .carousel-dots {
                        bottom: 10px;
                    }

                    .carousel-dot {
                        width: 8px;
                        height: 8px;
                    }
                }
            `}</style>
        </div>
    )
}